// ═══════════════════════════════════════════════════════════════════
// TREASURY PAGE — الخزينة المالية (v3.3.576)
//
// Reads from the local Daftra cache (accounting_journal_accounts +
// accounting_transactions) and re-displays it through a treasury lens:
//   • 3 KPI cards + 1 grand total
//   • Donut (composition) + line trend (last 6 months)
//   • 3 collapsible sections (cash boxes, banks, undeposited checks)
//   • Latest-movements table with sub-tabs
//   • Slide-over drill-down per account (KPIs + combo chart + tx history)
//
// Sync is NOT performed here — that lives in /accounting. We just display
// the last-synced timestamp and link the user there.
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { treasury } from "../services/api";
import toast from "react-hot-toast";
import {
  FiDollarSign, FiHome, FiCreditCard, FiFileText, FiRefreshCw,
  FiDownload, FiX, FiChevronDown, FiChevronLeft, FiClock,
  FiTrendingUp, FiTrendingDown, FiArrowUpRight, FiPrinter,
} from "react-icons/fi";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RTooltip, Legend,
  LineChart, Line, CartesianGrid, XAxis, YAxis,
  ComposedChart, Bar, ReferenceLine,
} from "recharts";

// ───────── Helpers ─────────
const fmt = (n) => (n == null || isNaN(n)) ? "0" : Math.round(Number(n)).toLocaleString("en-US");
const fmtShort = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1_000_000) return (v / 1_000_000).toFixed(1) + "م";
  if (Math.abs(v) >= 1_000) return (v / 1_000).toFixed(0) + "ك";
  return String(Math.round(v));
};
const fmtDate = (d) => d ? new Date(d).toISOString().slice(0, 10) : "—";
const fmtDateTime = (d) => {
  if (!d) return "—";
  const x = new Date(d);
  return x.toISOString().slice(0,10) + " " + x.toTimeString().slice(0,5);
};

const COLORS = {
  total:  "#4f46e5",  // indigo
  cash:   "#10b981",  // emerald
  banks:  "#0ea5e9",  // sky
  checks: "#f59e0b",  // amber
  green:  "#22c55e",
  red:    "#ef4444",
};

// ─── Small UI primitives ───
function KpiCard({ stripe, icon, label, value, suffix, sub, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: "white", borderRadius: 12, border: "1px solid #e2e8f0",
      overflow: "hidden", boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      cursor: onClick ? "pointer" : "default",
      transition: "box-shadow 0.15s, transform 0.15s",
    }} onMouseEnter={e => onClick && (e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)")}
       onMouseLeave={e => onClick && (e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.04)")}>
      <div style={{ height: 4, background: stripe }} />
      <div style={{ padding: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>
            {label}
          </div>
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: stripe + "15", color: stripe,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
          }}>
            {icon}
          </div>
        </div>
        <div style={{ marginTop: 12, fontSize: 26, fontWeight: 800, color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>
          {value}
        </div>
        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{suffix}</div>
        {sub && <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>{sub}</div>}
      </div>
    </div>
  );
}

function Section({ id, icon, title, subtitle, total, totalColor, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    if (id) window["openSection_" + id] = () => setOpen(true);
  }, [id]);
  return (
    <div id={id} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
      <div onClick={() => setOpen(!open)} style={{
        padding: "14px 20px", cursor: "pointer",
        display: "flex", alignItems: "center", gap: 12,
        background: open ? "#f8fafc" : "white",
        borderBottom: open ? "1px solid #e2e8f0" : "none",
        transition: "background 0.15s",
      }}>
        <span style={{ color: "#94a3b8", transition: "transform 0.2s", transform: open ? "rotate(90deg)" : "none" }}>
          <FiChevronLeft />
        </span>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: "#0f172a" }}>{title}</div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 1 }}>{subtitle}</div>
        </div>
        {total != null && (
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>الإجمالي</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: totalColor || "#0f172a", fontVariantNumeric: "tabular-nums" }}>
              {fmt(total)} <span style={{ fontSize: 11, color: "#94a3b8" }}>ر.س</span>
            </div>
          </div>
        )}
      </div>
      {open && <div>{children}</div>}
    </div>
  );
}

// ───────── Main page ─────────
export default function TreasuryPage() {
  const [summary, setSummary] = useState(null);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [checkAccounts, setCheckAccounts] = useState([]);
  const [trend, setTrend] = useState([]);
  const [movements, setMovements] = useState([]);
  const [movementsTotal, setMovementsTotal] = useState(0);
  const [movCat, setMovCat] = useState("all");
  const [movDays, setMovDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, b, k, t] = await Promise.all([
        treasury.summary(),
        treasury.accounts({ type: "cash"   }),
        treasury.accounts({ type: "banks"  }),
        treasury.accounts({ type: "checks" }),
        treasury.trend({ months: 6 }),
      ]);
      setSummary(s.data);
      setCashAccounts(c.data.items || []);
      setBankAccounts(b.data.items || []);
      setCheckAccounts(k.data.items || []);
      setTrend(t.data.items || []);
    } catch (e) {
      toast.error("فشل تحميل بيانات الخزينة: " + (e?.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMovements = useCallback(async () => {
    try {
      const r = await treasury.movements({ cat: movCat, days: movDays, limit: 50 });
      setMovements(r.data.items || []);
      setMovementsTotal(r.data.total || 0);
    } catch (e) { /* keep silent — non-critical */ }
  }, [movCat, movDays]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadMovements(); }, [loadMovements]);

  // Donut data
  const donutData = useMemo(() => summary ? [
    { name: "البنوك",  value: summary.banksTotal,  fill: COLORS.banks  },
    ...(summary.escrowTotal ? [{ name: "حساب الضمان", value: summary.escrowTotal, fill: "#7c3aed" }] : []), /* v3.4.568 */
    { name: "الخزنات", value: summary.cashTotal,   fill: COLORS.cash   },
    { name: "شيكات",   value: summary.checksTotal, fill: COLORS.checks },
  ].filter(d => d.value > 0) : [], [summary]);

  // Trend (recharts wants ascending dates; backend already returns ascending)
  const trendData = useMemo(() => trend.map(p => ({
    label: new Date(p.month).toLocaleDateString("ar-SA", { month: "short", year: "2-digit" }),
    cash:   Number(p.cash)   || 0,
    banks:  Number(p.banks)  || 0,
    checks: Number(p.checks) || 0,
    total:  Number(p.total)  || 0,
  })), [trend]);

  // Auto-detect "check age" for accounts under 1220010 — these aren't really
  // separate accounts; we display the transactions on this single account as
  // "checks" in the UI. For the section table here we show only the rolled-up
  // 1220010 row + delegate per-check view to the drill-down.

  return (
    <div style={{ maxWidth: 1280, margin: "0 auto", padding: "20px 24px" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
            <FiDollarSign /> الخزينة المالية
          </h1>
          <p style={{ fontSize: 13, color: "#64748b", margin: "6px 0 0 0" }}>
            إجمالي السيولة والشيكات غير المودَعة عبر كل الحسابات
            {summary?.lastSyncAt && (
              <>
                {" "}•{" "}
                <FiClock style={{ verticalAlign: "middle" }} />
                {" "}<span style={{ color: "#475569" }}>آخر مزامنة من دفترة: <span style={{ fontFamily: "monospace" }}>{fmtDateTime(summary.lastSyncAt)}</span></span>
                {" "}<a href="/accounting" style={{ color: "#4f46e5", textDecoration: "none" }}>← الذهاب لصفحة المزامنة</a>
              </>
            )}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={loadAll} disabled={loading} style={{
            padding: "8px 14px", fontSize: 13, border: "1px solid #cbd5e1",
            background: "white", borderRadius: 8, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 6,
          }}>
            <FiRefreshCw style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
            تحديث
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 18 }}>
        <KpiCard
          stripe={COLORS.total}
          icon={<FiDollarSign />}
          label="إجمالي الخزينة"
          value={summary ? fmt(summary.totalTreasury) : "—"}
          suffix="ر.س"
          sub={summary?.vsLastMonthPct != null ? (
            <span style={{ color: summary.vsLastMonthPct >= 0 ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
              {summary.vsLastMonthPct >= 0 ? <FiTrendingUp/> : <FiTrendingDown/>} {Math.abs(summary.vsLastMonthPct).toFixed(1)}%{" "}
              <span style={{ color: "#64748b", fontWeight: 400 }}>عن الشهر الماضي</span>
            </span>
          ) : null}
        />
        <KpiCard
          stripe={COLORS.cash}
          icon={<FiHome />}
          label="الخزنات (كاش)"
          value={summary ? fmt(summary.cashTotal) : "—"}
          suffix="ر.س"
          sub={summary && <><strong>{summary.cashCount}</strong> صناديق</>}
          onClick={() => window.openSection_sec_cash && window.openSection_sec_cash()}
        />
        <KpiCard
          stripe={COLORS.banks}
          icon={<FiCreditCard />}
          label="البنوك"
          value={summary ? fmt(summary.banksTotal) : "—"}
          suffix="ر.س"
          sub={summary && <><strong>{summary.banksCount}</strong> حسابات{summary.escrowTotal ? " — بلا حساب الضمان" : ""}</>}
          onClick={() => window.openSection_sec_banks && window.openSection_sec_banks()}
        />
        {summary?.escrowTotal != null && (summary.escrowTotal !== 0 || summary.escrowCount > 0) && (/* v3.4.568 — حساب الضمان مفصول عن البنوك وضمن الإجمالي */
          <KpiCard
            stripe="#7c3aed"
            icon={<FiCreditCard />}
            label="حساب الضمان — بارك 2"
            value={fmt(summary.escrowTotal)}
            suffix="ر.س"
            sub={<>{summary.escrowNames || "حساب الضمان"} — <span style={{ color: "#7c3aed", fontWeight: 600 }}>ضمن إجمالي الخزينة</span></>}
            onClick={() => window.openSection_sec_banks && window.openSection_sec_banks()}
          />
        )}
        <KpiCard
          stripe={COLORS.checks}
          icon={<FiFileText />}
          label="شيكات لم تودع"
          value={summary ? fmt(summary.checksTotal) : "—"}
          suffix="ر.س"
          sub={summary && (
            <>
              قيد التحصيل
              {summary.overdueChecksCount > 0 && (
                <> • <span style={{ color: "#dc2626", fontWeight: 600 }}>{summary.overdueChecksCount} متأخرة</span></>
              )}
            </>
          )}
          onClick={() => window.openSection_sec_checks && window.openSection_sec_checks()}
        />
      </div>

      {/* Charts row */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 2fr)", gap: 14, marginBottom: 18 }}>
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>التوزيع النسبي</div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 8 }}>نسبة كل فئة من إجمالي الخزينة</div>
          <div style={{ height: 220 }}>
            {donutData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={donutData} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="85%" paddingAngle={2}>
                    {donutData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <RTooltip formatter={(v) => fmt(v) + " ر.س"} />
                  <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>لا توجد بيانات</div>}
          </div>
        </div>
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>تطور إجمالي الخزينة</div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 8 }}>المجموع شهرياً، مع التفصيل حسب الفئة (6 شهور)</div>
          <div style={{ height: 220 }}>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData}>
                  <CartesianGrid stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtShort} />
                  <RTooltip formatter={(v) => fmt(v) + " ر.س"} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="total"  name="الإجمالي" stroke={COLORS.total}  strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="banks"  name="البنوك"   stroke={COLORS.banks}  strokeWidth={1.5} strokeDasharray="5 3" dot={false} />
                  <Line type="monotone" dataKey="cash"   name="الخزنات"  stroke={COLORS.cash}   strokeWidth={1.5} strokeDasharray="5 3" dot={false} />
                  <Line type="monotone" dataKey="checks" name="شيكات"    stroke={COLORS.checks} strokeWidth={1.5} strokeDasharray="5 3" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>لا توجد بيانات</div>}
          </div>
        </div>
      </div>

      {/* Sections */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 14 }}>
        <Section
          id="sec_cash"
          icon={<FiHome />}
          title="الخزنات (كاش)"
          subtitle={`${summary?.cashCount || 0} صناديق`}
          total={summary?.cashTotal}
          totalColor={COLORS.cash}
        >
          <AccountTable rows={cashAccounts} kind="cash" onClick={(r) => setSelectedAccount({ ...r, kind: "cash" })} />
        </Section>

        <Section
          id="sec_banks"
          icon={<FiCreditCard />}
          title="البنوك"
          subtitle={`${summary?.banksCount || 0} حساب نشط`}
          total={summary?.banksTotal}
          totalColor={COLORS.banks}
        >
          <AccountTable rows={bankAccounts} kind="bank" onClick={(r) => setSelectedAccount({ ...r, kind: "bank" })} />
        </Section>

        <Section
          id="sec_checks"
          icon={<FiFileText />}
          title="الشيكات (لم تودع)"
          subtitle={summary?.overdueChecksCount > 0
            ? <>قيد التحصيل • <span style={{ color: "#dc2626" }}>{summary.overdueChecksCount} متأخرة</span></>
            : "قيد التحصيل"}
          total={summary?.checksTotal}
          totalColor={COLORS.checks}
        >
          <AccountTable rows={checkAccounts} kind="check" onClick={(r) => setSelectedAccount({ ...r, kind: "check" })} />
          <div style={{ padding: "10px 20px", fontSize: 12, color: "#64748b", borderTop: "1px solid #f1f5f9", background: "#fffbeb" }}>
            💡 تفاصيل الشيكات الفردية (رقم/ساحب/استحقاق) موجودة داخل قيود الحساب — اضغط على الصف لعرضها.
          </div>
        </Section>

        {/* Latest movements */}
        <Section
          id="sec_movements"
          icon={<FiClock />}
          title="آخر الحركات"
          subtitle={`آخر ${movDays} أيام • ${fmt(movementsTotal)} حركة`}
          defaultOpen={true}
        >
          <div style={{ borderBottom: "1px solid #f1f5f9", padding: "8px 20px", display: "flex", gap: 16, alignItems: "center" }}>
            {[
              { k: "all",    label: "الكل" },
              { k: "cash",   label: "الخزنات" },
              { k: "banks",  label: "البنوك" },
              { k: "checks", label: "الشيكات" },
            ].map(t => (
              <button key={t.k} onClick={() => setMovCat(t.k)} style={{
                padding: "6px 0", background: "transparent", border: "none",
                borderBottom: movCat === t.k ? "2px solid #4f46e5" : "2px solid transparent",
                color: movCat === t.k ? "#4f46e5" : "#64748b",
                fontWeight: movCat === t.k ? 700 : 400, cursor: "pointer",
                fontFamily: "inherit", fontSize: 13,
              }}>
                {t.label}
              </button>
            ))}
            <div style={{ marginInlineStart: "auto" }}>
              <select value={movDays} onChange={e => setMovDays(parseInt(e.target.value))} style={{
                padding: "4px 8px", fontSize: 12, border: "1px solid #cbd5e1",
                borderRadius: 6, fontFamily: "inherit",
              }}>
                <option value={1}>اليوم فقط</option>
                <option value={7}>آخر 7 أيام</option>
                <option value={30}>آخر 30 يوم</option>
                <option value={90}>آخر 90 يوم</option>
              </select>
            </div>
          </div>
          <MovementsTable rows={movements} onAccountClick={(code) => setSelectedAccount({ code, kind: "any" })} />
        </Section>
      </div>

      {/* Slide-over */}
      {selectedAccount && (
        <AccountDetailSlideOver
          account={selectedAccount}
          onClose={() => setSelectedAccount(null)}
        />
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

// ───────── Subcomponents ─────────

function AccountTable({ rows, kind, onClick }) {
  if (!rows || rows.length === 0) {
    return <div style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>لا توجد حسابات</div>;
  }
  const Th = ({ children, style }) => (
    <th style={{ padding: "10px 16px", fontSize: 11, color: "#64748b", textTransform: "uppercase",
                 letterSpacing: 0.4, fontWeight: 600, textAlign: "right", background: "#f8fafc",
                 borderBottom: "1px solid #e2e8f0", ...style }}>{children}</th>
  );
  const Td = ({ children, style }) => (
    <td style={{ padding: "10px 16px", fontSize: 13, borderBottom: "1px solid #f1f5f9", ...style }}>{children}</td>
  );
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <Th>الرقم</Th>
            <Th>الاسم</Th>
            <Th>آخر حركة</Th>
            <Th style={{ textAlign: "left" }}>الرصيد (ر.س)</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.code} onClick={() => onClick(r)} style={{ cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                onMouseLeave={e => e.currentTarget.style.background = "white"}>
              <Td style={{ color: "#64748b", fontFamily: "monospace" }}>{r.code}</Td>
              <Td style={{ fontWeight: 500 }}>{r.name}</Td>
              <Td style={{ color: "#64748b", fontFamily: "monospace" }}>{fmtDate(r.last_movement_date)}</Td>
              <Td style={{ textAlign: "left", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmt(r.balance)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MovementsTable({ rows, onAccountClick }) {
  if (!rows || rows.length === 0) {
    return <div style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>لا توجد حركات في هذه الفترة</div>;
  }
  const Th = ({ children, style }) => (
    <th style={{ padding: "10px 16px", fontSize: 11, color: "#64748b", textTransform: "uppercase",
                 letterSpacing: 0.4, fontWeight: 600, textAlign: "right", background: "#f8fafc",
                 borderBottom: "1px solid #e2e8f0", ...style }}>{children}</th>
  );
  const Td = ({ children, style }) => (
    <td style={{ padding: "10px 16px", fontSize: 13, borderBottom: "1px solid #f1f5f9", ...style }}>{children}</td>
  );
  const catIcon = (cat) => cat === "cash" ? <FiHome/> : cat === "banks" ? <FiCreditCard/> : cat === "checks" ? <FiFileText/> : null;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <Th>التاريخ</Th>
            <Th>الحساب</Th>
            <Th>البيان</Th>
            <Th>المرجع</Th>
            <Th style={{ textAlign: "left" }}>المبلغ</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map(m => {
            const net = Number(m.net) || 0;
            const isIn = net > 0;
            return (
              <tr key={m.id} onClick={() => onAccountClick(m.journal_account_code)} style={{ cursor: "pointer", position: "relative" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                  onMouseLeave={e => e.currentTarget.style.background = "white"}>
                <Td style={{ position: "relative", color: "#64748b", fontFamily: "monospace" }}>
                  <span style={{
                    position: "absolute", right: 0, top: 0, bottom: 0, width: 3,
                    background: isIn ? "#22c55e" : "#ef4444",
                  }} />
                  <span style={{ paddingRight: 6 }}>{fmtDate(m.journal_date)}</span>
                </Td>
                <Td style={{ fontWeight: 500 }}>
                  <span style={{ color: m.category === "cash" ? COLORS.cash : m.category === "banks" ? COLORS.banks : COLORS.checks, marginInlineEnd: 6, verticalAlign: "middle" }}>
                    {catIcon(m.category)}
                  </span>
                  <span style={{ color: "#64748b", fontSize: 11, fontFamily: "monospace", marginInlineEnd: 4 }}>{m.journal_account_code}</span>
                  {m.journal_account_name}
                </Td>
                <Td style={{ color: "#475569", maxWidth: 380, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {m.related_description || m.journal_description || "—"}
                </Td>
                <Td style={{ color: "#4f46e5", fontSize: 11, fontFamily: "monospace" }}>{m.journal_number || "—"}</Td>
                <Td style={{ textAlign: "left", fontWeight: 600, fontVariantNumeric: "tabular-nums", color: isIn ? "#16a34a" : "#dc2626" }}>
                  {isIn ? "+" : ""}{fmt(net)}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ───────── Slide-over (account detail) ─────────
export function AccountDetailSlideOver({ account, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (!account?.code) return;
    setLoading(true);
    const to = new Date().toISOString().slice(0, 10);
    const fromD = new Date(); fromD.setDate(fromD.getDate() - days);
    const from = fromD.toISOString().slice(0, 10);
    treasury.accountDetails(account.code, { from, to, limit: 100 })
      .then(r => setData(r.data))
      .catch(e => toast.error("فشل تحميل التفاصيل: " + (e?.response?.data?.error || e.message)))
      .finally(() => setLoading(false));
  }, [account?.code, days]);

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  const kind = account?.kind || "any";
  const stripe = kind === "cash" ? COLORS.cash : kind === "bank" ? COLORS.banks : kind === "check" ? COLORS.checks : COLORS.total;
  const Icon = kind === "cash" ? FiHome : kind === "bank" ? FiCreditCard : kind === "check" ? FiFileText : FiDollarSign;

  // Build combo chart data
  const comboData = useMemo(() => (data?.dailyFlow || []).map(p => ({
    date: fmtDate(p.date),
    debit: Number(p.debit) || 0,
    credit: -(Number(p.credit) || 0),
    balance: Number(p.balance) || 0,
  })), [data]);

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,.45)",
        zIndex: 40, opacity: 1, transition: "opacity .3s",
      }} />
      <aside style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "min(900px, 70vw)", background: "white", zIndex: 50,
        boxShadow: "-4px 0 24px rgba(0,0,0,.12)", overflowY: "auto",
        transform: "translateX(0)", transition: "transform .3s",
      }}>
        {/* Sticky header */}
        <div style={{ position: "sticky", top: 0, background: "white", borderBottom: "1px solid #e2e8f0", zIndex: 10 }}>
          <div style={{ padding: "12px 24px", display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={onClose} style={{ width: 32, height: 32, border: "none", background: "transparent", borderRadius: 6, cursor: "pointer", color: "#64748b" }}
              onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <FiX size={18} />
            </button>
            <div style={{ marginInlineStart: "auto", fontSize: 12 }}>
              <button onClick={() => window.print()} style={{ background: "transparent", border: "none", color: "#4f46e5", cursor: "pointer", fontSize: 12 }}>
                <FiPrinter style={{ verticalAlign: "middle" }} /> طباعة
              </button>
            </div>
          </div>
          <div style={{ padding: "0 24px 14px" }}>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <div style={{
                width: 48, height: 48, borderRadius: 10, background: stripe + "15", color: stripe,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0,
              }}>
                <Icon />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>
                  {data?.account?.name || account.name || "—"}
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                  <span style={{ fontFamily: "monospace" }}>{account.code}</span>
                  {data?.account?.type && <> • {data.account.type}</>}
                  {data?.account?.currency && <> • {data.account.currency}</>}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ padding: 24 }}>
          {loading && !data && <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>جاري التحميل…</div>}

          {data && (
            <>
              {/* Mini KPIs */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 20 }}>
                <MiniStat label="رصيد افتتاحي" value={fmt(data.kpis.openingBalance)} sub={fmtDate(data.kpis.from)} bg="#f8fafc" />
                <MiniStat label="مدين خلال الفترة" value={"+ " + fmt(data.kpis.debitsInPeriod)} sub={null} bg="#f0fdf4" color="#15803d" />
                <MiniStat label="دائن خلال الفترة" value={"- " + fmt(data.kpis.creditsInPeriod)} sub={null} bg="#fef2f2" color="#b91c1c" />
                <MiniStat label="الرصيد الحالي" value={fmt(data.kpis.currentBalance)}
                  sub={data.kpis.pctChange != null ? (
                    <span style={{ color: data.kpis.pctChange >= 0 ? "#16a34a" : "#dc2626" }}>
                      {data.kpis.pctChange >= 0 ? "▲" : "▼"} {Math.abs(data.kpis.pctChange).toFixed(1)}%
                    </span>
                  ) : null}
                  bg="#eef2ff" color="#3730a3"
                />
              </div>

              {/* Filters */}
              <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
                <select value={days} onChange={e => setDays(parseInt(e.target.value))} style={{
                  padding: "6px 10px", fontSize: 12, border: "1px solid #cbd5e1",
                  borderRadius: 6, fontFamily: "inherit",
                }}>
                  <option value={30}>آخر 30 يوم</option>
                  <option value={90}>آخر 90 يوم</option>
                  <option value={180}>آخر 6 شهور</option>
                  <option value={365}>هذه السنة</option>
                </select>
              </div>

              {/* Combo chart */}
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14, marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>تطور الرصيد + الحركات اليومية</div>
                    <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>الخط = الرصيد التراكمي • الأعمدة = الحركة اليومية</div>
                  </div>
                  <div style={{ display: "flex", gap: 12, fontSize: 11, color: "#475569" }}>
                    <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#22c55e", borderRadius: 2, marginInlineEnd: 4 }}/>مدين</span>
                    <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#ef4444", borderRadius: 2, marginInlineEnd: 4 }}/>دائن</span>
                    <span><span style={{ display: "inline-block", width: 18, height: 2, background: "#4f46e5", verticalAlign: "middle", marginInlineEnd: 4 }}/>الرصيد</span>
                  </div>
                </div>
                <div style={{ height: 240 }}>
                  {comboData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={comboData}>
                        <CartesianGrid stroke="#e2e8f0" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis yAxisId="left" tick={{ fontSize: 10 }} tickFormatter={fmtShort} />
                        <YAxis yAxisId="right" orientation="left" tick={{ fontSize: 10 }} tickFormatter={fmtShort} />
                        <RTooltip formatter={(v, name) => [fmt(Math.abs(v)) + " ر.س", name]} />
                        <ReferenceLine y={0} yAxisId="left" stroke="#cbd5e1" />
                        <Bar  yAxisId="left"  dataKey="debit"  name="مدين"  fill="#22c55e" radius={[2,2,0,0]} />
                        <Bar  yAxisId="left"  dataKey="credit" name="دائن"  fill="#ef4444" radius={[2,2,0,0]} />
                        <Line yAxisId="right" type="monotone" dataKey="balance" name="الرصيد" stroke="#4f46e5" strokeWidth={2} dot={{ r: 2 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  ) : <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>لا توجد حركات في هذه الفترة</div>}
                </div>
              </div>

              {/* Transactions */}
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a", marginBottom: 10 }}>
                  سجل الحركات التفصيلي
                  <span style={{ color: "#94a3b8", fontWeight: 400, marginInlineStart: 8 }}>({data.totalTransactions} حركة)</span>
                </div>
                <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: "#f8fafc" }}>
                        <th style={{ padding: "8px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>التاريخ</th>
                        <th style={{ padding: "8px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>المرجع</th>
                        <th style={{ padding: "8px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>البيان</th>
                        <th style={{ padding: "8px 12px", textAlign: "left",  fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>مدين</th>
                        <th style={{ padding: "8px 12px", textAlign: "left",  fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>دائن</th>
                        <th style={{ padding: "8px 12px", textAlign: "left",  fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>الرصيد</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.transactions.map(t => (
                        <tr key={t.id} style={{ borderTop: "1px solid #f1f5f9" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                            onMouseLeave={e => e.currentTarget.style.background = "white"}>
                          <td style={{ padding: "8px 12px", color: "#64748b", fontFamily: "monospace" }}>{fmtDate(t.date)}</td>
                          <td style={{ padding: "8px 12px", color: "#4f46e5", fontFamily: "monospace", fontSize: 11 }}>{t.ref || "—"}</td>
                          <td style={{ padding: "8px 12px", color: "#475569" }}>{t.memo || "—"}</td>
                          <td style={{ padding: "8px 12px", textAlign: "left", color: t.debit > 0 ? "#16a34a" : "#cbd5e1", fontWeight: t.debit > 0 ? 600 : 400, fontVariantNumeric: "tabular-nums" }}>
                            {t.debit > 0 ? "+ " + fmt(t.debit) : "—"}
                          </td>
                          <td style={{ padding: "8px 12px", textAlign: "left", color: t.credit > 0 ? "#dc2626" : "#cbd5e1", fontWeight: t.credit > 0 ? 600 : 400, fontVariantNumeric: "tabular-nums" }}>
                            {t.credit > 0 ? "- " + fmt(t.credit) : "—"}
                          </td>
                          <td style={{ padding: "8px 12px", textAlign: "left", color: "#475569", fontSize: 12, fontVariantNumeric: "tabular-nums" }}>{fmt(t.balance)}</td>
                        </tr>
                      ))}
                      {data.transactions.length === 0 && (
                        <tr><td colSpan={6} style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>لا توجد حركات في هذه الفترة</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {data.totalTransactions > data.transactions.length && (
                  <div style={{ textAlign: "center", padding: 12, fontSize: 12, color: "#64748b" }}>
                    عرض {data.transactions.length} من أصل {data.totalTransactions} حركة
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

function MiniStat({ label, value, sub, bg = "#f8fafc", color = "#0f172a" }) {
  return (
    <div style={{ background: bg, borderRadius: 8, padding: 10, border: "1px solid #e2e8f0" }}>
      <div style={{ fontSize: 10, color: "#64748b" }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2, color, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
